import { z } from "zod";

import { api } from "@/redux/api";

import { PendingLeadRow, settledListPersonIds } from "./scoring-pending.helper";

import type { RootState } from "@/redux";

export const PendingLeadScoringResponseSchema = z.object({
	listPersonIds: z.array(z.number()),
});

export type PendingLeadScoringResponse = z.infer<typeof PendingLeadScoringResponseSchema>;

export interface PendingLeadScoringRequest {
	listId: number;
	leads: PendingLeadRow[];
}

/**
 * Which of the rows on screen still have lead scoring queued (PREC-4076).
 *
 * Tagged `ListPerson` on purpose, twice over: it *is* per-list-person state, so the bulk re-score's
 * existing invalidation refreshes it the moment the trigger returns — the POST only answers once
 * every event row exists, so that refetch is guaranteed to see them, and no optimistic client-side
 * guess is needed. And when a lead drops out of the pending set its score has just changed on the
 * server, so the rows are pulled again; otherwise the spinner would stop and uncover the old score.
 */
export const scoringPendingApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getPendingLeadScoring: builder.query<PendingLeadScoringResponse, PendingLeadScoringRequest>({
			query: (body) => ({
				url: "score/pending-leads",
				method: "POST",
				body,
				responseSchema: PendingLeadScoringResponseSchema,
			}),
			providesTags: ["ListPerson"],
			async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
				const previous = scoringPendingApi.endpoints.getPendingLeadScoring.select(arg)(
					getState() as RootState
				)?.data?.listPersonIds;

				try {
					const { data } = await queryFulfilled;

					if (settledListPersonIds(previous, data.listPersonIds).length) {
						dispatch(api.util.invalidateTags(["ListPerson"]));
					}
				} catch {
					// The poll failed — offline, a 5xx, or the component unmounted while it was in
					// flight. There is nothing to reconcile and the next tick retries, so the only
					// thing an uncaught rejection here buys is noise: `queryFulfilled` rejects on
					// every failed request, and with no catch that surfaces as an unhandled promise
					// rejection (which is also what fails the web test run, where no server answers).
				}
			},
		}),
	}),
});

export const { useGetPendingLeadScoringQuery } = scoringPendingApi;
